import { useEffect } from "react";

import type {
    RadioProps as _RadioProps,
    RadioGroupProps as _RadioGroupProps,
} from "@chakra-ui/react";
import { forwardRef, Radio as _Radio, RadioGroup as _Radiogroup, Text } from "@chakra-ui/react";

export interface RadioProps extends _RadioProps {}
export interface RadioGroupProps extends _RadioGroupProps {
    selectedBorderWidth?: string;
}

// Hack to get around the fact that Chakra's Radio prior to version ^2 have a bug where it...
// ...focus the radio when it is clicked. We only want to focus when tabing with the keyboard.
const removeDataFocusAttr = () => {
    requestAnimationFrame(() => {
        document.querySelector(".chakra-radio__control[data-focus]")?.removeAttribute("data-focus");
    });
};

export const RadioGroup = forwardRef<RadioGroupProps, "div">(
    ({ children, onMouseDown, onMouseUp, ...props }, ref) => {
        useEffect(() => {
            window.addEventListener("focus", removeDataFocusAttr);
            return () => {
                window.removeEventListener("focus", removeDataFocusAttr);
            };
        }, []);

        return (
            <_Radiogroup
                ref={ref}
                {...props}
                onMouseDown={(e) => {
                    removeDataFocusAttr();
                    onMouseDown?.(e);
                }}
                onMouseUp={(e) => {
                    removeDataFocusAttr();
                    onMouseUp?.(e);
                }}
            >
                {children}
            </_Radiogroup>
        );
    }
);

export const Radio = forwardRef<RadioProps, "input">(
    ({ borderColor, borderRadius, height, width, sx, children, ...props }, ref) => {
        const isTextOnly = typeof children === "string";

        const controlWidth = typeof width === "number" ? `${width}px` : "18px";
        const controlHeight = typeof height === "number" ? `${height}px` : "18px";

        return (
            <_Radio
                ref={ref}
                style={{
                    width: controlWidth,
                    height: controlHeight,
                }}
                h={height === "auto" ? "auto" : undefined}
                w={width === "auto" ? "auto" : undefined}
                borderColor={borderColor || "black"}
                borderRadius={borderRadius || "100%"}
                _hover={{
                    outline: "4px solid",
                    outlineColor: "gray.150",
                }}
                alignItems={"start"}
                _checked={{
                    borderWidth: "4px",
                    height: controlHeight,
                    width: controlWidth,
                }}
                {...props}
            >
                <Text
                    as={isTextOnly ? "span" : "div"}
                    sx={sx}
                    transition="textDecoration 0.2s ease"
                    _hover={{
                        textDecorationColor: "black",
                    }}
                >
                    {children}
                </Text>
            </_Radio>
        );
    }
);
